import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { useMe } from "@/auth/use-me";
import { BackButton } from "@/components/common/BackButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { AddPlaceholder } from "@/components/design/AddPlaceholder";
import { BoxHeader } from "@/components/design/BoxHeader";
import { SaveStatus } from "@/components/match/SaveStatus";
import { Button } from "@/components/design/Button";
import { SaveGroupNameDialog } from "@/components/save/SaveCollectionTemplate";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { Permission, useHasPermission } from "@/auth/access-permissions";
import { useAgentStore } from "@/stores/agent-store";
import { useGrowNoteRuns } from "@/stores/grow-note-store";
import { downloadBlob } from "@/lib/file-download";
import { notebookFileName } from "@/lib/studio-notebook-file-names";
import { askToMarkdown, exportMarkdown, notebookNotes, notebookSources } from "./notebook-markdown";
import { StudioExportDialog, type StudioExportFormat } from "./StudioExportDialog";
import { StudioNotebookPdfExport, type StudioNotebookPdfJob } from "./StudioNotebookPdfExport";
import { useStudioNotebooks, type StudioNotebook } from "./studio-notebooks";
import { StudioNotebookEditor } from "./StudioNotebookEditor";
import { StudioNotebookStream } from "./StudioNotebookStream";
import { StudioNotebookTitle } from "./StudioNotebookTitle";
import { Download, FilePen, FileText, FolderInput, UserPlus } from "lucide-react";
import type { StudioToolAction } from "./StudioToolPills";
import { useOrganizationalRecordAccess } from "@/pages/organizational-record/use-organizational-record-access";

export function StudioPage() {
  const me = useMe();
  return me.id ? <StudioWorkspace key={me.id} /> : <div className="p-6 text-smycles-body text-ink-muted"><FormattedMessage id="common.loading" defaultMessage="Loading…" /></div>;
}

function StudioWorkspace() {
  const intl = useIntl();
  const [params, setParams] = useSearchParams();
  const notebookId = params.get("notebook");
  const state = useStudioNotebooks(notebookId);
  const pageRef = useRef<HTMLDivElement>(null);
  // Back on the list the page starts at the top; an open notebook starts at its end, which the stream does itself.
  useEffect(() => { if (!notebookId) pageRef.current?.scrollIntoView?.({ block: "start" }); }, [notebookId]);
  const book = state.notebooks.find((item) => item.id === notebookId);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // The notebook whose full text is open in the editor. Kept by id, so a notebook opened from elsewhere (the
  // Grow save chip) never starts in another one's editor.
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = editingId !== null && editingId === notebookId;
  const [exporting, setExporting] = useState(false);
  // The PDF being made, if any: the page holds its one hidden renderer (@adr SPARK-0062-studio-notebooks-export-as-pdf@1).
  // One at a time. The renderer keeps what it sent for its one job, so the jobs are counted and each mounts its own.
  const [pdfJob, setPdfJob] = useState<StudioNotebookPdfJob | null>(null);
  const [pdfJobNumber, setPdfJobNumber] = useState(0);
  // A PDF that could not be made, said where the person is when it fails, until the next export succeeds or
  // starts, or until they open another notebook or go back to the list (spark#1252).
  const [pdfFailed, setPdfFailed] = useState<{ notebookId: string | null } | null>(null);
  if (pdfFailed && pdfFailed.notebookId !== notebookId) setPdfFailed(null);
  // The format the export dialog chose for a final version, by the id of the prompt that writes it: that one's
  // own export starts there, every other one at PDF (@adr SPARK-0062-studio-notebooks-export-as-pdf@1). Forgotten
  // once the person leaves the notebook, back to the list and into the same one again included.
  const [finalFormats, setFinalFormats] = useState<{ notebookId: string | null; formats: ReadonlyMap<string, StudioExportFormat> }>({ notebookId, formats: new Map() });
  if (finalFormats.notebookId !== notebookId) setFinalFormats({ notebookId, formats: new Map() });
  // The Grow save chip's note request writing this notebook's own parts, and what it wrote until this page has
  // taken it over (@adr SPARK-0060-a-saved-grow-note-is-first-written-as-its-own-fallback@1). Meanwhile the
  // notebook is held still: nothing renames, deletes, edits or adds to it.
  const growNote = useGrowNoteRuns((s) => (book ? s.running[book.id] : undefined));
  const growWritten = useGrowNoteRuns((s) => (book ? s.written[book.id]?.text : undefined));
  const locked = Boolean(growNote) || (growWritten !== undefined && growWritten !== book?.text);
  const notebookWritable = state.writable && !locked;
  const askInNotebook = useAgentStore((s) => s.askInNotebook);
  const pending = useAgentStore((s) => s.pending);
  // An open notebook rides every chat turn as data (@external-adr AGENT-SERVICE-0036-the-studio-proposes-records-the-user-decides);
  // the list view only says how many notebooks there are.
  useAssistantContext(book ? null : [{
    description: "Studio notebooks: unstructured research notes, not Person or Company records",
    value: `${state.notebooks.length} notebooks.`,
  }]);
  const setStudioNotebook = useAgentStore((s) => s.setStudioNotebook);
  // The assistant reads the notebook as numbered passages, so a proposal can be checked against the passage it
  // cites: the user's text, and the answers kept in it (a find from a search can become a proposal next).
  const sources = book ? notebookSources(book.text) : [];
  const openNotebookKey = book ? JSON.stringify([book.name, sources]) : "";
  useEffect(() => {
    if (!openNotebookKey) return;
    const [name, sources] = JSON.parse(openNotebookKey) as [string, { id: string; text: string }[]];
    setStudioNotebook({ name, notes: sources });
    return () => setStudioNotebook(null);
  }, [openNotebookKey, setStudioNotebook]);
  const canWritePerson = useHasPermission(Permission.WritePerson);
  const canCreateCompany = useHasPermission(Permission.CreateCompany);
  const canPropose = canWritePerson || canCreateCompany;
  const canFileRecords = useOrganizationalRecordAccess().canWrite;
  const askForFinal = (id: string) => askInNotebook(id, intl.formatMessage({ id: "studio.finalPrompt", defaultMessage: "Write a final version of this notebook as Markdown from everything in it: my notes, the documents I brought in and the answers kept here, duplicates merged, ordered by topic, nothing they say left out and nothing added. Reply with the Markdown only." }), "final");
  // The assistant's actions among the composer's pills: each a prompt in the notebook, offered only to someone
  // who may do what it leads to.
  const emptyHint = intl.formatMessage({ id: "studio.tools.emptyHint", defaultMessage: "First bring something into the notebook: a note, a document or an answer" });
  const aiActions = (id: string): StudioToolAction[] => [
    ...(canPropose ? [{
      id: "studio-to-records",
      label: intl.formatMessage({ id: "studio.toRecords", defaultMessage: "Turn into records" }),
      hint: intl.formatMessage({ id: "studio.toRecordsHint", defaultMessage: "Propose people and companies" }),
      icon: UserPlus,
      disabled: !sources.length,
      disabledHint: emptyHint,
      onSelect: () => askInNotebook(id, intl.formatMessage({ id: "studio.toRecordsPrompt", defaultMessage: "Propose records from this notebook." })),
    }] : []),
    ...(canFileRecords ? [{
      id: "studio-to-org-record",
      label: intl.formatMessage({ id: "studio.toOrgRecord", defaultMessage: "File in an organizational record" }),
      hint: intl.formatMessage({ id: "studio.toOrgRecordHint", defaultMessage: "As a note about a person" }),
      icon: FolderInput,
      disabled: !sources.length,
      disabledHint: emptyHint,
      onSelect: () => askInNotebook(id, intl.formatMessage({ id: "studio.toOrgRecordPrompt", defaultMessage: "File the content of this notebook as a note in an organizational record. Ask me which person if it is not clear." })),
    }] : []),
    {
      id: "studio-final",
      label: intl.formatMessage({ id: "studio.finalVersion", defaultMessage: "Write a final version" }),
      hint: intl.formatMessage({ id: "studio.finalVersionHint", defaultMessage: "Merged and ordered, as Markdown" }),
      icon: FileText,
      // From all of the notebook's material, notes, documents and kept answers alike: the action waits only for an empty notebook.
      disabled: !sources.length,
      disabledHint: emptyHint,
      onSelect: () => askForFinal(id),
    },
  ];
  const notebookActions: StudioToolAction[] = [
    {
      id: "studio-edit",
      label: intl.formatMessage({ id: "studio.editText", defaultMessage: "Edit text" }),
      icon: FilePen,
      disabled: !notebookWritable,
      onSelect: () => setEditingId(notebookId),
    },
    {
      id: "studio-export",
      label: intl.formatMessage({ id: "studio.export", defaultMessage: "Export" }),
      icon: Download,
      onSelect: () => setExporting(true),
    },
  ];
  // One PDF at a time: both ways to start one are disabled while a job runs (@adr SPARK-0062-studio-notebooks-export-as-pdf@1).
  const exportPdf = (job: StudioNotebookPdfJob) => {
    setPdfFailed(null);
    setPdfJobNumber((number) => number + 1);
    setPdfJob(job);
  };
  const downloadExport = (notebook: StudioNotebook, withAnswers: boolean, format: StudioExportFormat) => {
    const markdown = exportMarkdown(notebook, { withAnswers, proposalLabel: intl.formatMessage({ id: "studio.proposalLabel", defaultMessage: "Proposal" }), documentLabel: intl.formatMessage({ id: "studio.documentLabel", defaultMessage: "Document" }) });
    if (format === "pdf") exportPdf({ name: notebook.name, markdown, variant: withAnswers ? "withAnswers" : "notesOnly" });
    else {
      downloadBlob(new Blob([markdown], { type: "text/markdown" }), notebookFileName(notebook.name));
      setPdfFailed(null);
    }
  };
  const openNotebook = (id?: string) => {
    setEditingId(null);
    const next = new URLSearchParams(params);
    if (id) next.set("notebook", id); else next.delete("notebook");
    setParams(next);
  };
  return <div ref={pageRef} id="studio-page" className="mt-8 max-w-[1600px] px-4 pb-12 @sm:px-6 @lg:px-10">
    <div className="mb-5"><BackButton /></div>
    <BoxHeader title={<FormattedMessage id="studio.title" defaultMessage="Studio" />} variant="hero" />
    <p className="smycles-field-meta text-ink"><FormattedMessage id="studio.description" defaultMessage="A place for loose thoughts, names and research finds." /></p>
    <p className="mt-2 smycles-field-meta text-ink-muted"><FormattedMessage id="studio.localNotice" defaultMessage="Notebooks are saved automatically and only you can see them. You can export any of them as Markdown or PDF at any time." /></p>
    {pdfJob ? <p id="studio-pdf-status" role="status" className="mt-2 smycles-field-meta text-ink-muted"><FormattedMessage id="studio.pdf.creating" defaultMessage="Creating the PDF…" /></p> : null}
    {pdfFailed ? <p id="studio-pdf-failed" role="alert" className="mt-2 smycles-field-meta text-ink"><FormattedMessage id="studio.pdf.failed" defaultMessage="The PDF could not be created. Please try again." /></p> : null}
    {state.error ? <p role="alert" className="mt-4 text-smycles-body text-ink-accent"><FormattedMessage id="studio.storageError" defaultMessage="Your notebooks could not be saved or loaded. Please keep a copy of your text and try again." /></p> : null}
    {state.loading ? <p className="mt-8 text-smycles-body text-ink-muted"><FormattedMessage id="common.loading" defaultMessage="Loading…" /></p>
    : notebookId && !book ? <div className="mt-8 space-y-4">
      <p id="studio-not-found"><FormattedMessage id="studio.notFound" defaultMessage="This notebook does not exist (any more)." /></p>
      <Button onClick={() => openNotebook()}><FormattedMessage id="studio.notebooks" defaultMessage="Notebooks" /></Button>
    </div> : book ? <section className="mt-8">
      <Button id="studio-back" variant="ghost" size="compact" onClick={() => openNotebook()}><FormattedMessage id="studio.back" defaultMessage="← All notebooks" /></Button>
      <div className="mt-4 max-w-4xl">
        {/* The name is renamed in place, deleting stays apart; what else the notebook offers sits in the pills. */}
        <BoxHeader wrap title={<StudioNotebookTitle
          key={`${book.id}:${book.name}`}
          name={book.name}
          otherNames={state.notebooks.filter((item) => item.id !== book.id).map((item) => item.name)}
          editable={notebookWritable}
          onRename={(name) => state.renameNotebook(book.id, name)}
        />} right={
          // The match editor's save status (@adr SPARK-0048-studio-notebook-saves-like-the-match-editor@2), calm unless saving or failed.
          <div className="flex items-center gap-3">
            <SaveStatus saveState={state.saveState} lastSavedAt={state.lastSavedAt} onRetry={state.retry} />
            <Button id="studio-delete" variant="ghost" size="compact" disabled={!notebookWritable} onClick={() => setDeleting(true)}><FormattedMessage id="common.delete" defaultMessage="Delete" /></Button>
          </div>
        } />
      </div>
      <div className="mt-2 max-w-4xl">
        {editing
          ? <StudioNotebookEditor key={book.id} text={book.text} disabled={!notebookWritable} onSave={(text) => state.saveText(book.id, text)} onDone={() => setEditingId(null)} />
          : <StudioNotebookStream key={book.id} book={book} writable={notebookWritable} growNote={growNote ?? null} onAppend={(text) => state.appendText(book.id, text)} onUndoAppend={(text) => state.undoAppend(book.id, text)} onKeepAsk={(ask) => state.appendText(book.id, askToMarkdown(ask))} onEditNote={(segment, expected, text) => state.editNote(book.id, segment, expected, text)} onEditCard={(askId, index, card) => state.editCard(book.id, askId, index, card)} aiActions={aiActions(book.id)} notebookActions={notebookActions} onExportPdf={exportPdf} onExportedMarkdown={() => setPdfFailed(null)} pdfBusy={Boolean(pdfJob)} finalFormats={finalFormats.formats} />}
      </div>
    </section> : <section className="mt-8">
      <BoxHeader title={<FormattedMessage id="studio.notebooks" defaultMessage="Notebooks" />} right={<span className="smycles-field-meta text-ink-muted">{state.notebooks.length}</span>} />
      <div id="studio-notebooks" className="flex flex-wrap items-start gap-4">
        {state.notebooks.map((notebook) => <button id={`studio-notebook-${notebook.id}`} key={notebook.id} type="button" onClick={() => openNotebook(notebook.id)}
          className="box flex h-32 w-72 max-w-full min-w-0 flex-col overflow-hidden bg-surface p-3 text-left transition hover:ring-ink/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40">
          <h2 className="line-clamp-2 break-words text-smycles-body font-medium leading-tight">{notebook.name}</h2>
          <p className="mt-1 line-clamp-2 whitespace-pre-wrap break-words text-smycles-label leading-snug text-ink-muted">{notebookNotes(notebook.text) || intl.formatMessage({ id: "studio.emptyPreview", defaultMessage: "Room for your next idea." })}</p>
          <div className="mt-auto flex flex-wrap items-center justify-end gap-2 pt-2 text-smycles-micro text-ink-muted">
            <time dateTime={new Date(notebook.updatedAt).toISOString()}>{intl.formatDate(notebook.updatedAt, { day: "numeric", month: "short" })}</time>
          </div>
        </button>)}
        <div className="h-32 w-72 max-w-full">
          <AddPlaceholder id="studio-create" fullWidth disabled={!state.writable} className="h-full" label={<FormattedMessage id="studio.newNotebook" defaultMessage="New notebook" />} onClick={() => setCreating(true)} />
        </div>
      </div>
    </section>}
    {creating ? <SaveGroupNameDialog open title={intl.formatMessage({ id: "studio.newNotebook", defaultMessage: "New notebook" })}
      initial=""
      existingNames={state.notebooks.map((item) => item.name)}
      placeholder={intl.formatMessage({ id: "studio.namePlaceholder", defaultMessage: "What are you collecting?" })}
      onClose={() => setCreating(false)}
      onSave={async (name) => {
        const id = await state.createNotebook(name);
        if (id) { setCreating(false); openNotebook(id); }
      }} /> : null}
    {book && exporting ? <StudioExportDialog
      open
      finalDisabled={!sources.length || pending || locked}
      pdfBusy={Boolean(pdfJob)}
      onClose={() => setExporting(false)}
      onWithAnswers={(format) => { downloadExport(book, true, format); setExporting(false); }}
      onNotesOnly={(format) => { downloadExport(book, false, format); setExporting(false); }}
      onFinal={(format) => {
        const askId = askForFinal(book.id);
        setFinalFormats((current) => ({ notebookId: current.notebookId, formats: new Map(current.formats).set(askId, format) }));
        setExporting(false);
      }}
    /> : null}
    {pdfJob ? <StudioNotebookPdfExport key={pdfJobNumber} job={pdfJob} onDone={() => setPdfJob(null)} onError={() => { setPdfJob(null); setPdfFailed({ notebookId }); }} /> : null}
    <ConfirmDialog id="studio-confirm-delete" open={deleting} title={intl.formatMessage({ id: "studio.deleteNotebook", defaultMessage: "Delete notebook?" })}
      message={intl.formatMessage({ id: "studio.deleteNotebookMessage", defaultMessage: "The notebook is deleted with all its notes." })}
      onCancel={() => setDeleting(false)} onConfirm={async () => { if (book && await state.deleteNotebook(book.id)) { setDeleting(false); openNotebook(); } }} />
  </div>;
}
